import {
    constants as dateTimeConstants,
    getDateFnsLocale,
} from '@eventbrite/datetime-fns';
import { getAttendeeTimezone } from '@eventbrite/event-date-time';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import { format } from 'date-fns-tz';
import React, { useEffect, useState } from 'react';
import { isEventEligibleForRepeatingEventsConsumerExperiment } from '../../../statsig';
import { ChildEventProps, LocalizedDateTime } from '../../../types';
import { useIsServerSideRendered } from '../../hooks/useIsServerSideRendered';
import './StartDate.scss';

import { findFirstAvailableEvent } from '../WhenAndWhere/DateAndTime/utils';

type StartDateStandardProps = {
    isOnlineEvent: boolean;
    localizedDateTimeStart: LocalizedDateTime;
    locale: string;
    isSeriesParent: boolean;
    childEvents: Array<ChildEventProps> | null;
};

type StartDateProps = {
    startDateTime: string;
    hideStartDate: boolean;
} & StartDateStandardProps;

const getFormattedStartDate = ({
    eventStartDateTime,
    isOnlineEvent,
    locale,
}: {
    eventStartDateTime: LocalizedDateTime;
    isOnlineEvent: boolean;
    locale: string;
}) => {
    const date = isOnlineEvent
        ? eventStartDateTime.utc
        : eventStartDateTime.local;
    const dateObj = new Date(date);
    const timeZone = isOnlineEvent
        ? getAttendeeTimezone() ?? eventStartDateTime.timezone
        : eventStartDateTime.timezone;
    const { DATE_FORMATS } = dateTimeConstants;
    const shortDate = format(dateObj, DATE_FORMATS[locale] || 'EEEE, MMMM d', {
        timeZone,
        locale: getDateFnsLocale(locale),
    });
    const dateTime = format(dateObj, 'yyy-MM-dd', {
        timeZone,
    });

    return { shortDate, dateTime };
};

export const StartDateStandard = ({
    isOnlineEvent,
    localizedDateTimeStart,
    locale,
    isSeriesParent,
    childEvents,
}: StartDateStandardProps) => {
    const isParentEventExperimentVariant =
        isEventEligibleForRepeatingEventsConsumerExperiment(
            isSeriesParent,
            childEvents,
        );

    let dateTime: LocalizedDateTime | null = null;

    switch (isParentEventExperimentVariant) {
        //Experiment Variant: Eligible events that falls into variant option (parent events with future events)
        case true:
            dateTime = findFirstAvailableEvent(childEvents)?.start ?? null;
            break;
        //Experiment Control: Eligible events that falls into control option / Non-eligible events for the experiment
        case false:
            dateTime = isSeriesParent ? null : localizedDateTimeStart;
            break;
    }

    const [formattedDates, setFormattedDates] = useState(
        dateTime === null
            ? null
            : getFormattedStartDate({
                  eventStartDateTime: dateTime,
                  isOnlineEvent,
                  locale,
              }),
    );

    useEffect(() => {
        if (dateTime !== null && isOnlineEvent) {
            setFormattedDates(
                getFormattedStartDate({
                    eventStartDateTime: dateTime,
                    isOnlineEvent,
                    locale,
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (HAS_WINDOW) {
        window.__i18n__ = { locale };
    }
    return (
        <>
            {formattedDates === null ? (
                <span className="start-date">{gettext('Multiple dates')}</span>
            ) : (
                <time
                    className="start-date"
                    dateTime={formattedDates?.dateTime}
                    data-testid="start-date-standard"
                >
                    {formattedDates?.shortDate}
                </time>
            )}
        </>
    );
};

export const StartDate = ({
    hideStartDate,
    isOnlineEvent,
    localizedDateTimeStart,
    locale,
    isSeriesParent,
    childEvents,
}: StartDateProps) => {
    const { isServerSideRendered } = useIsServerSideRendered();

    if ((isSeriesParent && isServerSideRendered) || hideStartDate) {
        return null;
    }

    return (
        <StartDateStandard
            isOnlineEvent={isOnlineEvent}
            localizedDateTimeStart={localizedDateTimeStart}
            locale={locale}
            isSeriesParent={isSeriesParent}
            childEvents={childEvents}
        />
    );
};
